// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * RFC 4180 CSV writer with a formula-injection guard.
 *
 * `rotate-these.csv` WILL BE OPENED IN EXCEL — that is what it is for. Any field starting `=`, `+`,
 * `-`, `@`, tab or CR is executed as a formula on open in Excel and Google Sheets, and both masked
 * values and provider strings can legitimately start with `-`.
 *
 * The guard is a leading apostrophe, which is the OWASP-standard mitigation. LibreOffice renders the
 * apostrophe literally — a cosmetic regression we accept over a live formula in a security report.
 * (Efficacy across every Excel/Sheets/Numbers version is not something we can verify from here.)
 *
 * Control characters become visible escapes so ONE ROW IS ALWAYS ONE PHYSICAL LINE. A findings CSV
 * whose row count differs from its line count cannot be `wc -l`'d or `grep`'d, which is most of what
 * anyone does with one.
 */

const DANGEROUS_LEAD = /^[=+\-@\t\r]/;

function escapeField(value: string): string {
  let v = value
    .replace(/\\/g, '\\\\')
    .replace(/\r/g, '\\r')
    .replace(/\n/g, '\\n')
    .replace(/\t/g, '\\t')
    // Any remaining C0/C1 control character.
    .replace(/[\u0000-\u001f\u007f-\u009f]/g, (c) => `\\x${c.charCodeAt(0).toString(16).padStart(2, '0')}`);

  const dangerous = DANGEROUS_LEAD.test(value);
  if (dangerous) v = `'${v}`;

  // `;` forces quoting for de/fr/es Excel locales, which use it as the separator.
  // A field that got the apostrophe guard is ALWAYS quoted as well: unquoted, the apostrophe is
  // just another character in the field and some importers strip or ignore it, which would put the
  // formula back. Quoting is what makes the guard survive the round trip.
  const needsQuote = dangerous || /[",;\n\r]/.test(v) || v !== v.trim();
  return needsQuote ? `"${v.replace(/"/g, '""')}"` : v;
}

/** Serialise rows to a CSV string with a UTF-8 BOM and CRLF line endings, as Excel expects. */
export function toCsv(headers: readonly string[], rows: readonly (readonly string[])[]): string {
  const lines = [headers.map(escapeField).join(',')];
  for (const row of rows) lines.push(row.map(escapeField).join(','));
  return `\ufeff${lines.join('\r\n')}\r\n`;
}

/** Escape text for inclusion in the self-contained HTML report. Never `innerHTML` with raw input. */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
